import { useMemo, useState } from 'react'
import { useCreateUser, useResetPassword, useUpdateUser, useUsers } from '../../api/queries'
import type { PortalUser } from '../../api/types'
import { useAuthenticatedSession } from '../../auth/session'
import { BffError } from '../../bff/errors'
import { useT, type Translate } from '../../i18n'
import type { BusinessTime } from '../../time/businessTime'
import {
  Button, DataTable, EmptyState, Skeleton, StatusBadge, useToast, type Column,
} from '../../ui'
import { InternalUsersNote } from './InternalUsersNote'
import { UserFormModal, type UserFormValues } from './UserFormModal'
import screen from '../screen.module.css'

/** Sorted client-side (plan D7): the API returns one biller's whole list —
 *  tens of rows, not thousands — so a round trip per sort is pure latency. */
const byName = (a: PortalUser, b: PortalUser): number =>
  a.full_name.localeCompare(b.full_name, undefined, { sensitivity: 'base' })

function buildColumns(
  t: Translate,
  businessTime: BusinessTime,
  currentUserEmail: string,
  actions: {
    readonly onEdit: (u: PortalUser) => void
    readonly onToggle: (u: PortalUser) => void
    readonly onReset: (u: PortalUser) => void
    readonly busyId: string | null
  },
): ReadonlyArray<Column<PortalUser>> {
  return [
    { key: 'name', header: t('users.name'), render: (u) => u.full_name },
    { key: 'email', header: t('users.email'), render: (u) => <span className={screen.mono}>{u.email}</span> },
    {
      key: 'status',
      header: t('users.status'),
      render: (u) => (
        <StatusBadge tone={u.enabled ? 'success' : 'neutral'}>
          {u.enabled ? t('users.active') : t('users.disabled')}
        </StatusBadge>
      ),
    },
    {
      key: 'mfa',
      header: t('users.mfa'),
      render: (u) => (
        <StatusBadge tone={u.mfa_enrolled ? 'success' : 'warning'}>
          {u.mfa_enrolled ? t('users.mfaOn') : t('users.mfaPending')}
        </StatusBadge>
      ),
    },
    {
      key: 'created',
      header: t('users.created'),
      render: (u) => (u.created_at ? businessTime.date(u.created_at) : '—'),
    },
    {
      key: 'actions',
      header: t('users.actions'),
      render: (u) => {
        // Acting on yourself is refused here AND by the API. The client-side
        // guard exists so the control is visibly unavailable rather than
        // failing after the click; the API's is the one that counts.
        const isSelf = u.email === currentUserEmail
        const busy = actions.busyId === u.id
        return (
          <div className={screen.actions}>
            <Button size="sm" variant="secondary" onClick={() => actions.onEdit(u)} disabled={busy}>
              {t('users.edit')}
            </Button>
            <Button
              size="sm"
              variant={u.enabled ? 'danger' : 'secondary'}
              onClick={() => actions.onToggle(u)}
              disabled={isSelf || busy}
              {...(isSelf ? { title: t('users.cannotActOnSelf') } : {})}
            >
              {u.enabled ? t('users.deactivate') : t('users.reactivate')}
            </Button>
            <Button size="sm" variant="ghost" onClick={() => actions.onReset(u)} disabled={busy}>
              {t('users.resetPassword')}
            </Button>
          </div>
        )
      },
    },
  ]
}

export function Users() {
  const t = useT()
  const { user, businessTime, canManageUsers } = useAuthenticatedSession()
  const { notify } = useToast()
  const { data, error, isPending, isFetching, refetch } = useUsers(canManageUsers)
  const createUser = useCreateUser()
  const updateUser = useUpdateUser()
  const resetPassword = useResetPassword()

  const [editing, setEditing] = useState<PortalUser | null>(null)
  const [formOpen, setFormOpen] = useState(false)
  const [busyId, setBusyId] = useState<string | null>(null)

  const rows = useMemo(() => (data?.users ?? []).toSorted(byName), [data?.users])

  const fail = (err: unknown) =>
    notify(
      'danger',
      err instanceof BffError && err.requestId
        ? `${err.message} — ${t('error.reference', { requestId: err.requestId })}`
        : err instanceof Error
          ? err.message
          : t('error.title'),
    )

  /** The API returns a warning when the account changed but the notification
   *  e-mail did not send. Reporting that as a plain success hides a user who
   *  will never receive their invitation. */
  const succeed = (message: string, warning: string) => {
    notify('success', message)
    if (warning) notify('danger', warning)
  }

  const submit = (values: UserFormValues) => {
    if (editing) {
      updateUser.mutate(
        { id: editing.id, changes: { first_name: values.first_name, last_name: values.last_name } },
        {
          onSuccess: (result) => {
            succeed(t('users.updated', { name: result.user.full_name }), result.warning)
            setFormOpen(false)
          },
          onError: fail,
        },
      )
      return
    }
    createUser.mutate(values, {
      onSuccess: (result) => {
        succeed(t('users.invited', { email: result.user.email }), result.warning)
        setFormOpen(false)
      },
      onError: fail,
    })
  }

  const toggle = (target: PortalUser) => {
    setBusyId(target.id)
    updateUser.mutate(
      { id: target.id, changes: { enabled: !target.enabled } },
      {
        onSuccess: (result) =>
          succeed(
            t(result.user.enabled ? 'users.reactivated' : 'users.deactivated', { name: result.user.full_name }),
            result.warning,
          ),
        onError: fail,
        onSettled: () => setBusyId(null),
      },
    )
  }

  const reset = (target: PortalUser) => {
    setBusyId(target.id)
    resetPassword.mutate(target.id, {
      onSuccess: () => notify('success', t('users.resetSent', { email: target.email })),
      onError: fail,
      onSettled: () => setBusyId(null),
    })
  }

  const columns = useMemo(
    () => buildColumns(t, businessTime, user.email, {
      onEdit: (u) => { setEditing(u); setFormOpen(true) },
      onToggle: toggle,
      onReset: reset,
      busyId,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t, businessTime, user.email, busyId],
  )

  /*
   * An internal user with no biller selected is not an error state.
   *
   * Users belong to a biller, so the API refuses without one — correctly. But
   * rendering that 400 as "Could not load this page" told an operator their
   * portal was broken, and the body quoted an HTTP header name at somebody who
   * has never seen one. The answer is a sentence and a pointer at the control
   * that fixes it, three feet to the left.
   */
  if (user.isInternal && !user.billerId) {
    return (
      <section>
        <div className={screen.header}>
          <div>
            <h1 className={screen.title}>{t('nav.users')}</h1>
          </div>
        </div>
        <EmptyState
          glyph="⌂"
          title={t('users.pickBillerTitle')}
          body={t('users.pickBillerBody')}
        />
        <InternalUsersNote />
      </section>
    )
  }

  if (!canManageUsers) {
    // Belt and braces with the route guard and the API's own 403. A user who
    // reaches this by typing the URL gets an explanation, not a blank screen.
    return <EmptyState glyph="⊘" title={t('users.forbiddenTitle')} body={t('users.forbiddenBody')} />
  }

  return (
    <>
      <div className={screen.header}>
        <div>
          <h1 className={screen.title}>{t('nav.users')}</h1>
          <p className={screen.subtitle}>
            {data ? t('users.scope', { biller: data.biller_id }) : t('common.loading')}
          </p>
        </div>
        <Button onClick={() => { setEditing(null); setFormOpen(true) }} disabled={isPending}>
          {t('users.newUser')}
        </Button>
      </div>

      {error ? (
        <EmptyState
          glyph="⚠"
          title={t('error.loadFailed')}
          body={error.message}
          actions={<Button onClick={() => void refetch()}>{t('error.retry')}</Button>}
        />
      ) : isPending ? (
        <Skeleton variant="row" count={5} label={t('users.loading')} />
      ) : rows.length === 0 ? (
        <EmptyState glyph="◍" title={t('users.emptyTitle')} body={t('users.emptyBody')} />
      ) : (
        <DataTable
          caption={t('users.caption', { count: rows.length })}
          columns={columns}
          rows={rows}
          rowKey={(u) => u.id}
          stale={isFetching}
          sortLabel={(column) => column}
        />
      )}

      {/* The realm guidance for INTERNAL accounts lives only on the
          all-billers empty state above. Here the operator is managing one
          biller's users — a different task with its own New user button — and
          instructions for a different kind of account underneath it read as
          part of this screen's workflow when they are not. */}
      <UserFormModal
        // Keyed so switching between users (or between edit and create) is a
        // fresh mount with fresh form state, instead of an effect re-seeding
        // it a render late — see the note in UserFormModal.
        key={editing?.id ?? 'new'}
        open={formOpen}
        user={editing}
        busy={createUser.isPending || updateUser.isPending}
        onSubmit={submit}
        onClose={() => setFormOpen(false)}
      />
    </>
  )
}
